import { useMemo, useState } from 'react';
import { Avatar, Button, Input, Table, Tag } from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { ReloadOutlined } from '@ant-design/icons';
import { ContextStatus, userRoleAsString } from '@leedpulse-monorepo/enums';
import { useNavigate } from 'react-router-dom';
import UserDoc from '../../../../domain/models/UserDoc';
import { advancedOnRow } from '../../../../core/utils/commonUtils';
import UserPartnerLogo from '../../../components/user_partner_logo/UserPartnerLogo';
import { USERS_PAGE_PATH } from '../../users/UsersPage';
import { useVoipEnabledUsers } from '../../../hooks/leedflow/useVoipEnabledUsers';

const VoipUsersTab: React.FC = () => {
  const navigate = useNavigate();
  const { users, status, reload } = useVoipEnabledUsers();
  const [search, setSearch] = useState<string>('');

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      `${user.firstname} ${user.lastname} ${user.email} ${user.phone}`.toLowerCase().includes(q),
    );
  }, [users, search]);

  const columns: TableColumnsType<UserDoc> = [
    {
      title: 'Avatar',
      dataIndex: 'avatar',
      key: 'avatar',
      render: (avatar: string) => <Avatar src={avatar} size="small" />,
    },
    {
      title: 'Utilisateur',
      key: 'user',
      render: (_, record) => (
        <>
          <UserPartnerLogo partnerSystem={record.partnerSystem} /> {record.firstname} {record.lastname}
        </>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '—',
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => userRoleAsString(role),
    },
    {
      title: 'VoIP',
      dataIndex: 'voip_enabled',
      key: 'voip_enabled',
      width: 100,
      render: (enabled: boolean) => <Tag color={enabled ? 'green' : 'default'}>{enabled ? 'Activée' : 'Désactivée'}</Tag>,
    },
    {
      title: 'Création',
      dataIndex: 'created',
      key: 'created',
      width: 160,
      render: (created: number) => (created ? dayjs(created).format('DD/MM/YYYY HH:mm') : '—'),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '0 0 1rem 0', alignItems: 'center' }}>
        <Input.Search
          allowClear
          placeholder="Nom, email ou téléphone"
          style={{ maxWidth: 280 }}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button type="primary" icon={<ReloadOutlined />} onClick={() => reload()}>
          Rafraîchir
        </Button>
      </div>

      <Table<UserDoc>
        rowKey="uid"
        size="small"
        columns={columns}
        dataSource={filteredUsers}
        loading={status === ContextStatus.LOADING}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        onRow={(record) =>
          advancedOnRow({
            url: `${USERS_PAGE_PATH}/${record.uid}`,
            navigate,
            className: 'table-row',
          })
        }
      />
    </>
  );
};

export default VoipUsersTab;
